import { useQueries, UseQueryResult } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import {
  getScansOptions,
  REPOSITORY_INTEGRATIONS_PAGE_SIZE,
  useGetAllRepositoryIntegrationsPages,
  useGetRepositoryIntegrations,
  useIntegrationTypesWithInstances,
} from '../../utils/api-client/api-hooks'
import { type ScanAggregateResponse, type Tool, ToolSchema } from '../../utils/api-client/api-schemas'
import { AI_SCANNER_TOOLS, getSelectableTools } from '../../utils/detector-utils'
import { convertIntegrationTypeToTool, getIsToolIntegrationType } from '../../utils/repository-utils'

export const useGetToolsForRepository = ({ repositoryId }: { repositoryId: string }) => {
  const { data: toolsWithIntegrations, isLoading: isToolsWithIntegrationsLoading } = useGetToolsWithIntegrations()
  const { data: toolsWithRepositoryIntegrations, isLoading: isToolsWithRepositoryIntegrationsLoading } =
    useGetToolsWithRepositoryIntegrations({ repositoryId })
  const { data: toolsWithScans, isLoading: isToolsWithScansLoading } = useGetToolsWithScans({ repositoryId })

  // Pixee's built-in scanners need no integration, so they are offered before their first scan.
  // Appended last so a repository's own tools still win the initial selection. The Labs filter
  // applies to every source, so a past AI scan does not resurface the tool with Labs off.
  const tools = useMemo(
    () =>
      new Set<Tool>(
        getSelectableTools([
          ...toolsWithIntegrations,
          ...toolsWithRepositoryIntegrations,
          ...toolsWithScans,
          ...AI_SCANNER_TOOLS,
        ])
      ),
    [toolsWithIntegrations, toolsWithRepositoryIntegrations, toolsWithScans]
  )

  return {
    data: tools,
    mappedTools: toolsWithRepositoryIntegrations,
    isLoading: isToolsWithIntegrationsLoading || isToolsWithRepositoryIntegrationsLoading || isToolsWithScansLoading,
  }
}

const useGetToolsWithScans = ({ repositoryId }: { repositoryId: string }) => {
  const queries = ToolSchema.options.map(tool =>
    getScansOptions({
      repositoryIds: [repositoryId],
      tools: [tool],
    })
  )

  const combine = useCallback(
    (results: UseQueryResult<ScanAggregateResponse>[]) => ({
      data: new Set(
        results
          .filter(
            (result): result is Extract<typeof result, { isSuccess: true }> =>
              result.isSuccess && result.data && result.data.total > 0
          )
          .map(result => result.data._embedded.items[0]!.detector)
      ),
      isLoading: results.some(result => result.isLoading),
    }),
    []
  )

  return useQueries({ queries, combine })
}

// "Which tools have an instance" is a question about the whole collection, which
// `useIntegrationTypesWithInstances` answers from `/api/v1/integration-types`. This used to read
// every page of the collection to derive the same set of tool names.
const useGetToolsWithIntegrations = () => {
  const { types, isLoading } = useIntegrationTypesWithInstances()
  const tools = useMemo(
    () => new Set(types.filter(getIsToolIntegrationType).map(convertIntegrationTypeToTool)),
    [types]
  )

  return { data: tools, isLoading }
}

const useGetToolsWithRepositoryIntegrations = ({ repositoryId }: { repositoryId: string }) => {
  const results = useGetAllRepositoryIntegrationsPages({ repositoryId })
  // `useGetAllPages` returns no results until its first page arrives, so `results` alone reads as
  // settled while that request is in flight. This shares the first page's query key, so it adds no request.
  const { isLoading: isFirstPageLoading } = useGetRepositoryIntegrations({
    repositoryId,
    page: { number: 0, size: REPOSITORY_INTEGRATIONS_PAGE_SIZE },
  })
  // Not memoized, unlike `useGetToolsWithIntegrations` above: `useGetAllPages` returns a fresh
  // `useQueries` array — of fresh tracked-result proxies — on every render, so a `useMemo` keyed on
  // it would recompute every render anyway. Stabilizing this means giving `useGetAllPages` a
  // `combine`, which needs its own change to that hook's declared return type.
  const tools = new Set(
    results.flatMap(result => result.data?._embedded.items ?? []).map(item => convertIntegrationTypeToTool(item.type))
  )

  return {
    data: tools,
    isLoading: isFirstPageLoading || results.some(result => result.isLoading),
  }
}
